import { useState } from 'react';
import Meta from '../components/Meta';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    cvr: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Navn skal udfyldes';
    }
    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Virksomhedsnavn skal udfyldes';
    }
    if (!formData.cvr.trim()) {
      newErrors.cvr = 'CVR-nummer skal udfyldes';
    } else if (!/^[0-9\s]{8,10}$/.test(formData.cvr.trim())) {
      newErrors.cvr = 'Indtast venligst et gyldigt CVR-nummer (8 cifre)';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer skal udfyldes';
    } else if (!/^[0-9+\s()-]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Indtast venligst et gyldigt telefonnummer';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail skal udfyldes';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Indtast venligst en gyldig e-mailadresse';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Beskrivelse skal udfyldes';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company_name: '',
          cvr: '',
          phone: '',
          email: '',
          description: '',
          website_url: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Fejl ved indsendelse af formular:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Meta 
        title="Kontakt" 
        description="Kontakt EnergiMælgere.dk i dag. Ring til os på 50 358 378 eller send en besked via vores formular for en uforpligtende gennemgang af jeres nuværende energiaftaler."
      />

      {/* Header Section */}
      <section className="bg-brand-dark text-white py-24 relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/57398cfb-6e5d-4ba8-a3b9-c24b8dc48392/b2b-energy-trading-office.jpg" 
            alt="Moderne B2B energihandel kontor" 
            className="w-full h-full object-cover object-center opacity-40"
          />
          <div className="absolute inset-0 bg-slate-950/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 space-y-4">
          <span className="text-xs font-bold uppercase tracking-widest text-white">Kontakt os</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight mb-6">
            Lad os tage en <span className="text-brand-blue-light">snak</span>
          </h1>
          <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl">
            Må vi kontakte jer for en uforpligtende gennemgang af jeres nuværende energiaftaler? Vi vurderer hurtigt, om der er et besparelsespotentiale – og hvad det konkret kan betyde for jeres bundlinje.
          </p>
        </div>
      </section>

      {/* Contact Content Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Contact Info & Map */}
          <div className="lg:col-span-5 space-y-10">
            <div className="space-y-6">
              <h2 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tight leading-tight">
                Kontaktoplysninger
              </h2>
              <p className="text-brand-muted text-sm leading-relaxed">
                Vi står altid til rådighed, hvis du har spørgsmål. Vi klarer alt det praktiske arbejdet, så du kan læne dig tilbage og se det positive resultat på bundlinjen.
              </p>
              <p className="text-brand-muted text-sm leading-relaxed">
                Du er altid velkommen til at kontakte os via telefon eller e-mail. Vi bestræber os på at besvare alle henvendelser inden for 24 timer på hverdage.
              </p>
            </div>

            <ul className="space-y-6 text-sm text-brand-muted">
              <li className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-sm bg-brand-light flex items-center justify-center text-brand-orange text-lg flex-shrink-0">
                  <i className="fa-regular fa-location-dot" />
                </div>
                <div>
                  <h3 className="font-bold text-brand-dark">Adresse</h3>
                  <p>EnergiMælgere.dk</p>
                  <p>Niels Jernes Vej 10</p>
                  <p>9220 Aalborg Ø</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-sm bg-brand-light flex items-center justify-center text-brand-orange text-lg flex-shrink-0">
                  <i className="fa-regular fa-phone" />
                </div>
                <div>
                  <h3 className="font-bold text-brand-dark">Telefon</h3>
                  <a href="tel:+4550358378" className="hover:text-brand-orange transition-colors whitespace-nowrap">
                    50 358 378
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-sm bg-brand-light flex items-center justify-center text-brand-orange text-lg flex-shrink-0">
                  <i className="fa-regular fa-envelope" />
                </div>
                <div>
                  <h3 className="font-bold text-brand-dark">E-mail</h3>
                  <a href="mailto:info@energimaeglere.dk" className="hover:text-brand-orange transition-colors whitespace-nowrap">
                    info@energimaeglere.dk
                  </a>
                </div>
              </li>
            </ul>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-7 bg-brand-light p-8 sm:p-10 rounded-sm border border-slate-100 shadow-sm">
            <div className="space-y-4 mb-8">
              <h2 className="text-2xl font-black text-slate-800 tracking-tight leading-tight">
                Send os en <span className="text-brand-orange">besked</span>
              </h2>
              <p className="text-brand-muted text-xs">
                Udfyld formularen nedenfor, så vender vi hurtigt tilbage til dig.
              </p>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-8 p-6 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-sm flex items-start gap-4 animate-in fade-in duration-300">
                <i className="fa-regular fa-circle-check text-2xl text-emerald-600 mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <h3 className="font-bold">Tak for din henvendelse!</h3>
                  <p className="text-xs">Vi har modtaget din besked og vender tilbage til dig hurtigst muligt.</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-8 p-6 bg-rose-50 border border-rose-200 text-rose-800 rounded-sm flex items-start gap-4 animate-in fade-in duration-300">
                <i className="fa-regular fa-circle-exclamation text-2xl text-rose-600 mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <h3 className="font-bold">Der skete en fejl</h3>
                  <p className="text-xs">Kunne ikke indsende formularen. Prøv venligst igen senere eller ring direkte til os.</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot field - absolutely hidden from humans */}
              <div className="sr-only absolute" aria-hidden="true">
                <label htmlFor="website_url">Hjemmeside URL (Udfyld ikke)</label>
                <input
                  id="website_url"
                  type="text"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              {/* Company Name & CVR Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label htmlFor="company_name" className="text-xs font-bold uppercase tracking-wider text-brand-dark">
                    Virksomhedsnavn <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="company_name"
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-sm border bg-white focus:outline-none focus:ring-2 focus:ring-brand-orange transition-all text-xs ${
                      errors.company_name ? 'border-rose-400 focus:ring-rose-200' : 'border-slate-200'
                    }`}
                    placeholder="Virksomhedens navn"
                  />
                  {errors.company_name && (
                    <p className="text-[10px] text-rose-600 font-bold uppercase tracking-wider">{errors.company_name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="cvr" className="text-xs font-bold uppercase tracking-wider text-brand-dark">
                    CVR-nummer <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="cvr"
                    type="text"
                    name="cvr"
                    value={formData.cvr}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-sm border bg-white focus:outline-none focus:ring-2 focus:ring-brand-orange transition-all text-xs ${
                      errors.cvr ? 'border-rose-400 focus:ring-rose-200' : 'border-slate-200'
                    }`}
                    placeholder="F.eks. 12345678"
                  />
                  {errors.cvr && (
                    <p className="text-[10px] text-rose-600 font-bold uppercase tracking-wider">{errors.cvr}</p>
                  )}
                </div>
              </div>

              {/* Name Field */}
              <div className="space-y-2">
                <label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-brand-dark">
                  Kontaktperson <span className="text-rose-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-sm border bg-white focus:outline-none focus:ring-2 focus:ring-brand-orange transition-all text-xs ${
                    errors.name ? 'border-rose-400 focus:ring-rose-200' : 'border-slate-200'
                  }`}
                  placeholder="Dit fulde navn"
                />
                {errors.name && (
                  <p className="text-[10px] text-rose-600 font-bold uppercase tracking-wider">{errors.name}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Phone Field */}
                <div className="space-y-2">
                  <label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-brand-dark">
                    Telefonnummer <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-sm border bg-white focus:outline-none focus:ring-2 focus:ring-brand-orange transition-all text-xs ${
                      errors.phone ? 'border-rose-400 focus:ring-rose-200' : 'border-slate-200'
                    }`}
                    placeholder="F.eks. 50 358 378"
                  />
                  {errors.phone && (
                    <p className="text-[10px] text-rose-600 font-bold uppercase tracking-wider">{errors.phone}</p>
                  )}
                </div>

                {/* Email Field */}
                <div className="space-y-2">
                  <label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-brand-dark">
                    E-mail <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-sm border bg-white focus:outline-none focus:ring-2 focus:ring-brand-orange transition-all text-xs ${
                      errors.email ? 'border-rose-400 focus:ring-rose-200' : 'border-slate-200'
                    }`}
                    placeholder="din@email.dk"
                  />
                  {errors.email && (
                    <p className="text-[10px] text-rose-600 font-bold uppercase tracking-wider">{errors.email}</p>
                  )}
                </div>
              </div>

              {/* Description Field */}
              <div className="space-y-2">
                <label htmlFor="description" className="text-xs font-bold uppercase tracking-wider text-brand-dark">
                  Beskrivelse <span className="text-rose-500">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="5"
                  value={formData.description}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-sm border bg-white focus:outline-none focus:ring-2 focus:ring-brand-orange transition-all resize-none text-xs ${
                    errors.description ? 'border-rose-400 focus:ring-rose-200' : 'border-slate-200'
                  }`}
                  placeholder="Beskriv din opgave eller dit spørgsmål..."
                />
                {errors.description && (
                  <p className="text-[10px] text-rose-600 font-bold uppercase tracking-wider">{errors.description}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-brand-orange hover:bg-brand-orange-hover disabled:bg-gray-400 text-white py-4 rounded-sm font-bold uppercase tracking-wider text-xs transition-colors duration-200 flex items-center justify-center gap-2 cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <i className="fa-regular fa-spinner-third animate-spin" /> Sender...
                  </>
                ) : (
                  <>
                    Send besked <i className="fa-regular fa-paper-plane text-xs" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
